'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsx as _jsx } from "react/jsx-runtime";
import PropTypes from 'prop-types';
import { cloneElement } from 'react';
import memoize from 'react-utils/memoize';
import styled, { css } from 'styled-components';
import classNames from 'classnames';
import { propTypeForSizes, toShorthandSize } from '../utils/propTypes/tshirtSize';
import { OLAF, SLINKY } from 'HubStyleTokens/colors';
import { BASE_LINE_HEIGHT, DRAG_HANDLE_WIDTH, DRAG_HANDLE_HEIGHT, DRAG_HANDLE_OFFSET_X, DRAG_HANDLE_OFFSET_Y } from 'HubStyleTokens/sizes';
import { setGrabCursor } from '../utils/Styles';
import { warnIfFragment } from '../utils/devWarnings';

const emptyBox = /*#__PURE__*/_jsx("span", {});

const maxHeightForSize = {
  sm: 10,
  md: 50,
  lg: 90
};
const getGrippieBackground = memoize(use => {
  const fill = use === 'on-dark' ? OLAF : SLINKY;
  const svgStr = `<svg width='${DRAG_HANDLE_WIDTH}' height='${DRAG_HANDLE_HEIGHT}' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${DRAG_HANDLE_WIDTH} ${DRAG_HANDLE_HEIGHT}'><path fill='${fill}' d='M0 0h2v2H0V0zm0 8h2v2H0V8zm0 8h2v2H0v-2zM0 4h2v2H0V4zm0 8h2v2H0v-2zm0 8h2v2H0v-2zM3 0h2v2H3V0zm0 8h2v2H3V8zm0 8h2v2H3v-2zM3 4h2v2H3V4zm0 8h2v2H3v-2zm0 8h2v2H3v-2z'/></svg>`; // prettier-ignore

  return css(["background-image:url(\"data:image/svg+xml,", "\");"], encodeURIComponent(svgStr));
});

const grippieBackgroundMixin = ({
  use
}) => getGrippieBackground(use);

const getGrippieOffset = memoize(offsetX => css(["background-position:", " calc(50% + 1px);background-repeat:no-repeat;"], offsetX));
const standaloneMixin = css(["display:inline-block;line-height:", ";min-width:", ";&::after{content:'.';visibility:hidden;}"], BASE_LINE_HEIGHT, DRAG_HANDLE_WIDTH);
const sizedMixin = css(["background-image:none !important;position:relative;&::before{content:' ';", ";background-repeat:repeat-y;position:absolute;", ":", ";top:0;width:", ";height:calc(100% - ", ");max-height:", "px;margin:", " 0;}"], grippieBackgroundMixin, ({
  trailing
}) => trailing ? 'right' : 'left', DRAG_HANDLE_OFFSET_X, DRAG_HANDLE_WIDTH, DRAG_HANDLE_HEIGHT, ({
  size
}) => maxHeightForSize[size], DRAG_HANDLE_OFFSET_Y);
const DragHandleChild = styled((_ref) => {
  let {
    children,
    className
  } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref.rest, ["_wrappedWithDnd"]);

  return /*#__PURE__*/cloneElement(children, Object.assign({
    className: classNames(children.props.className, className)
  }, rest));
}).withConfig({
  displayName: "UIDragHandle__DragHandleChild",
  componentId: "n5jh4t-0"
})(["", ";", ";", ";", ";", ""], setGrabCursor(), grippieBackgroundMixin, ({
  standalone,
  trailing
}) => {
  let offsetX = DRAG_HANDLE_OFFSET_X;

  if (standalone && trailing) {
    offsetX = '0';
  } else if (standalone) {
    offsetX = '50%';
  }

  if (trailing) offsetX = `calc(100% - ${offsetX})`;
  return getGrippieOffset(offsetX);
}, ({
  standalone
}) => standalone && standaloneMixin, ({
  size
}) => size !== 'default' && sizedMixin);

function UIDragHandle(props) {
  const {
    children,
    className,
    position,
    size,
    use
  } = props,
        rest = _objectWithoutPropertiesLoose(props, ["children", "className", "position", "size", "use"]);

  warnIfFragment(children, UIDragHandle.displayName);
  return /*#__PURE__*/_jsx(DragHandleChild, {
    className: classNames(className, 'is--draggable', size !== 'default' && 'is--draggable--sized'),
    rest: rest,
    size: toShorthandSize(size),
    standalone: children === emptyBox,
    trailing: position === 'end',
    use: use,
    children: children
  });
}

UIDragHandle.defaultProps = {
  children: emptyBox,
  position: 'start',
  size: 'default',
  use: 'default'
};
UIDragHandle.propTypes = {
  children: PropTypes.element.isRequired,
  position: PropTypes.oneOf(['start', 'end']).isRequired,
  size: PropTypes.oneOfType([propTypeForSizes(['sm', 'md', 'lg']), PropTypes.oneOf(['default'])]).isRequired,
  use: PropTypes.oneOf(['default', 'on-dark']).isRequired
};
UIDragHandle.displayName = 'UIDragHandle';
export default UIDragHandle;